import React from "react";
import {IProtocolStep, IProtocolStepKind, IProtocolStepStatus} from "@/protocols/types/protocol";
import {StepZoneAwareDateTimePicker} from "@/protocols/studies/steps/StepZoneAwareDateTimePicker";
import classes from "./StepTitleBar.module.css";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {TWorkflow} from "@/protocols/common/steps/StepView";
import {Box, Text} from "@mantine/core";
import {StepIcon} from "@/protocols/common/StepIcon.tsx";
import cx from "clsx";
import {StepStatusIcon} from "@/protocols/studies/steps/StepStatusIcon.tsx";
import dayjs from "dayjs";

/**
 * Props for StepStartEndTimeView component
 */
export interface StepTitleBarProps {
    /** The protocol step containing startTime/endTime */
    step: IProtocolStep;
    /** Current workflow mode */
    workflow?: TWorkflow;
    /** Optional timezone for datetime display */
    timezone?: string;
    /** Callback when step times are updated */
    onChange: (updates: DeepPartial<IProtocolStep>) => void;
    focusMode?: boolean
    titleComponent?: React.ReactNode; // Optional custom title component
}

/**
 * View component for displaying start/end times in protocol steps.
 *
 * Behavior:
 * - When workflow is "planning" or "session": shows only start_time (editable in session)
 * - When workflow is "review" or "review_edit": shows both start_time and end_time
 *
 * Readonly state:
 * - "planning": readonly
 * - "session": editable
 * - "review": readonly
 * - "review_edit": editable
 */
export const StepTitleBar: React.FC<StepTitleBarProps> = ({
                                                              step,
                                                              workflow = "session",
                                                              timezone,
                                                              onChange,
                                                              focusMode,
                                                              titleComponent
                                                          }) => {
    const readOnly = workflow === "planning" || workflow === "review";
    const isPlanningMode = workflow === "planning";
    const isGroup = step.kind === IProtocolStepKind.GROUP;
    const showStartTime = step.kind !== IProtocolStepKind.GROUP && workflow !== "planning";
    const showEndTime = step.kind !== IProtocolStepKind.GROUP && workflow === "review" || workflow === "review_edit";

    const handleStatusChange = (status: IProtocolStepStatus) => {
        switch (workflow) {
            case "session":
                if (status === step.status) {
                    onChange({status: IProtocolStepStatus.INCOMPLETE});
                } else {
                    onChange({status, endTime: dayjs().tz(timezone!, false),});
                }
                break;
            case "review_edit":
                if (status === step.status) {
                    // can't set it to incomplete in review_edit
                } else {
                    onChange({status});
                }
                break;
            default:
                break;
        }
    };

    // In planning mode, don't render anything for time
    if (isPlanningMode) {
        return <div data-testid={"startTime"}/>;
    }
    return (
        <Box className={cx(classes.root)}>
            <Box className={classes.stepIcon}>
                <StepIcon kind={step.kind}/>
            </Box>
            {showStartTime ? <StepZoneAwareDateTimePicker
                dateTimeProps={
                    {
                        description: "Start Time"
                    }
                }
                miw={"12em"}
                timezone={timezone!}
                step={step}
                onChange={onChange}
                propertyName="startTime"
                readonly={readOnly}
            /> : <Box/>
            }
            {titleComponent ? titleComponent : !focusMode ?
                <Text className={classes.stepName} fw={500}>{step.name}</Text> : <Box/>}
            {showEndTime ? <StepZoneAwareDateTimePicker
                    miw={"12em"}
                    timezone={timezone!}
                    step={step}
                    onChange={onChange}
                    propertyName="endTime"
                    readonly={readOnly}
                    dateTimeProps={
                        {
                            description: "End Time"
                        }
                    }
                />
                : <Box/>
            }
            {!isGroup ? <StatusToggle status={step.status} onChange={handleStatusChange} workflow={workflow}/> : <Box/>}
        </Box>
    )
};

/**
 * Renders status as 3 small buttons for quick selection
 */
const StatusToggle: React.FC<{
    status: IProtocolStepStatus;
    onChange: (status: IProtocolStepStatus) => void;
    workflow?: TWorkflow;
}> = ({status, onChange, workflow}) => {
    const readonly = workflow === "planning" || workflow === "review";

    return (
        <Box className={cx(classes.status)}>
            <Text className={cx(classes.statusLabel)}>Status</Text>
            <Box className={classes.statusToggle}>
                <StepStatusIcon status={IProtocolStepStatus.COMPLETED}
                                selected={status === IProtocolStepStatus.COMPLETED}
                                style={{cursor: readonly ? "default" : "pointer"}}
                                onChange={onChange}
                />
                <StepStatusIcon status={IProtocolStepStatus.SKIPPED} selected={status === IProtocolStepStatus.SKIPPED}
                                style={{cursor: readonly ? "default" : "pointer"}}
                                onChange={onChange}/>
            </Box>
        </Box>
    );
};